import React, { useState } from "react";
import {
  Button,
  Card,
  CardContent,
  FormControl,
  TextField,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { getBillingDeploymentReport } from "../../services/Dashboard";
import { getColumnsData } from "./DeploymentsReportColumns";
import { isValidDate } from "../../services/Utils";
import SearchIcon from "@mui/icons-material/Search";
import OTAPagination from "../OTAPagination";

const DEPLOYMENT_REPORT_STATUS = [
  { label: "Installation Completed", value: "INSTALLATION_COMPLETED" },
  { label: "Download Completed", value: "DOWNLOAD_COMPLETED" },
];

const DeploymentsReport = (props) => {
  const columns = getColumnsData();
  const [status, setStatus] = useState(DEPLOYMENT_REPORT_STATUS[0].value);

  const [startPicker, setStartPicker] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1, 0, 0, 0, 0),
  );
  const [endPicker, setEndPicker] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1, 0, 0, 0, 0),
  );

  const [startPickerErrorText, setStartPickerErrorText] = useState(null);
  const [endPickerErrorText, setEndPickerErrorText] = useState(null);

  const [searchObj, setSearchObj] = useState({
    status,
    startMonth: startPicker.getMonth() + 1,
    startYear: startPicker.getFullYear(),
    endMonth: endPicker.getMonth() + 1,
    endYear: endPicker.getFullYear(),
  });

  const handleStartMonthChange = (val = null) => {
    if (!isValidDate(new Date(val))) {
      setStartPickerErrorText("Invalid Month!");
      return;
    }
    setStartPickerErrorText(null);
    setStartPicker(val);
  };

  const handleEndMonthChange = (val = null) => {
    if (!isValidDate(new Date(val))) {
      setEndPickerErrorText("Invalid Month!");
      return;
    }
    if (new Date(val) < new Date(startPicker)) {
      setEndPickerErrorText("End month must be after start month!");
      return;
    }
    setEndPickerErrorText(null);
    setEndPicker(val);
  };

  const handleStatusChange = (event) => {
    const {
      target: { value: newStatus },
    } = event;
    setStatus(newStatus);
    setSearchObj({
      status: newStatus,
      startMonth: startPicker.getMonth() + 1,
      startYear: startPicker.getFullYear(),
      endMonth: endPicker.getMonth() + 1,
      endYear: endPicker.getFullYear(),
    });
  };

  const hasErrors = !!startPickerErrorText || !!endPickerErrorText;

  const processSearch = () => {
    if (
      !isValidDate(new Date(startPicker)) ||
      !isValidDate(new Date(endPicker))
    )
      return;
    if (new Date(endPicker) < new Date(startPicker)) return;
    setSearchObj({
      status,
      startMonth: startPicker.getMonth() + 1,
      startYear: startPicker.getFullYear(),
      endMonth: endPicker.getMonth() + 1,
      endYear: endPicker.getFullYear(),
    });
  };

  return (
    <div className="fragmentContainer">
      <Grid container spacing={2}>
        <Grid item xs={12} md={12} lg={12}>
          <Card sx={{ height: "96px" }}>
            <CardContent className="tile-cards">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <FormControl variant="standard" sx={{ m: 1, minWidth: 220 }}>
                    <InputLabel>Deployment Status</InputLabel>
                    <Select
                      value={status}
                      onChange={handleStatusChange}
                      label="Deployment Status"
                    >
                      {DEPLOYMENT_REPORT_STATUS.map((item) => (
                        <MenuItem key={item.value} value={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
                    <DatePicker
                      disableFuture
                      label="Start Month"
                      views={["month", "year"]}
                      value={startPicker}
                      timezone="UTC"
                      onChange={(val) => handleStartMonthChange(val)}
                      renderInput={(params) => (
                        <TextField
                          size="small"
                          {...params}
                          variant="standard"
                          error={!!startPickerErrorText}
                          onKeyDown={(e) => e.preventDefault()}
                          helperText={startPickerErrorText}
                        />
                      )}
                      maxDate={endPicker || new Date()}
                      minDate={new Date("01-01-2023")}
                      required
                    />
                  </FormControl>

                  <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
                    <DatePicker
                      disableFuture
                      label="End Month"
                      views={["month", "year"]}
                      value={endPicker}
                      timezone="UTC"
                      onChange={(val) => handleEndMonthChange(val)}
                      renderInput={(params) => (
                        <TextField
                          size="small"
                          {...params}
                          variant="standard"
                          error={!!endPickerErrorText}
                          onKeyDown={(e) => e.preventDefault()}
                          helperText={endPickerErrorText}
                        />
                      )}
                      maxDate={new Date()}
                      minDate={startPicker || new Date("01-01-2023")}
                      required
                    />
                  </FormControl>

                  <Button
                    disabled={hasErrors}
                    sx={{ ml: "8px" }}
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    onClick={() => processSearch()}
                  >
                    Search
                  </Button>
                </div>
              </LocalizationProvider>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={12} lg={12}>
          <Card>
            <CardContent
              sx={{
                padding: 0,
                ":last-child": { padding: 0 },
              }}
            >
              <OTAPagination
                key={status}
                columns={columns}
                endpointFn={getBillingDeploymentReport}
                queryName={`fetchBillingDeploymentsReport_${status}`}
                sortingMode="server"
                filterMode="server"
                sx={{ height: "calc(100vh - 200px)" }}
                sortByDefault="date"
                extraFilters={searchObj}
                getRowId={(row) =>
                  row?.id ||
                  `${row?.serialNo || ""}-${row?.vehicleKey || ""}-${row?.deploymentName || ""}-${row?.date || ""}`
                }
                exportCSV
                tableTitle="Deployments Report"
                {...props}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default DeploymentsReport;
